import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ArrowRight, Link2, Unlink } from 'lucide-react';
import { Button } from '../UI/button';
import { Badge } from '../UI/badge';
import { useLocale } from '../../contexts/LocaleContext';
import { cn } from '../../lib/utils';

function ConnectorLines({ links, containerRef }) {
  const [lines, setLines] = useState([]);

  const recompute = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;

    const containerRect = container.getBoundingClientRect();
    const nextLines = links
      .map((link) => {
        const sourceEl = container.querySelector(`[data-source-id="${link.sourceId}"]`);
        const targetEl = container.querySelector(`[data-target-id="${link.targetId}"]`);
        if (!sourceEl || !targetEl) return null;

        const sourceRect = sourceEl.getBoundingClientRect();
        const targetRect = targetEl.getBoundingClientRect();

        return {
          id: link.id,
          x1: sourceRect.right - containerRect.left,
          y1: sourceRect.top + sourceRect.height / 2 - containerRect.top,
          x2: targetRect.left - containerRect.left,
          y2: targetRect.top + targetRect.height / 2 - containerRect.top,
        };
      })
      .filter(Boolean);

    setLines(nextLines);
  }, [links, containerRef]);

  useEffect(() => {
    recompute();
    window.addEventListener('resize', recompute);
    return () => window.removeEventListener('resize', recompute);
  }, [recompute]);

  if (lines.length === 0) return null;

  return (
    <svg className="pointer-events-none absolute inset-0 z-0 h-full w-full" aria-hidden>
      {lines.map((line) => (
        <path
          key={line.id}
          d={`M ${line.x1} ${line.y1} C ${(line.x1 + line.x2) / 2} ${line.y1}, ${(line.x1 + line.x2) / 2} ${line.y2}, ${line.x2} ${line.y2}`}
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          className="text-primary-400"
        />
      ))}
    </svg>
  );
}

function CsvColumnConnector({
  headers,
  dtoFields,
  mapping,
  onMappingChange,
  disabled,
  fieldLabels = {},
  requiredFields = null,
}) {
  const { t } = useLocale();
  const containerRef = useRef(null);
  const [pendingSource, setPendingSource] = useState(null);

  const usedHeaders = useMemo(
    () => new Set(Object.values(mapping || {}).filter(Boolean)),
    [mapping]
  );

  const headerIndexByName = useMemo(() => {
    const map = new Map();
    headers.forEach((header, index) => map.set(header, index));
    return map;
  }, [headers]);

  const links = useMemo(
    () =>
      dtoFields
        .filter((field) => mapping?.[field])
        .map((field) => {
          const headerName = mapping[field];
          const sourceIndex = headerIndexByName.get(headerName);
          return {
            id: `${field}-${headerName}`,
            sourceId: sourceIndex != null ? `src-${sourceIndex}` : null,
            targetId: `tgt-${field}`,
          };
        })
        .filter((link) => link.sourceId),
    [dtoFields, mapping, headerIndexByName]
  );

  const handleSourceClick = (header) => {
    if (disabled) return;
    setPendingSource((prev) => (prev === header ? null : header));
  };

  const handleTargetClick = (field) => {
    if (disabled) return;
    if (pendingSource) {
      onMappingChange?.({ ...mapping, [field]: pendingSource });
      setPendingSource(null);
      return;
    }
    if (mapping?.[field]) {
      const next = { ...mapping };
      delete next[field];
      onMappingChange?.(next);
    }
  };

  const clearAll = () => {
    onMappingChange?.({});
    setPendingSource(null);
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-text-secondary">{t('csvMapperConnectHint')}</p>
        {Object.keys(mapping || {}).length > 0 ? (
          <Button type="button" variant="ghost" size="sm" onClick={clearAll} disabled={disabled}>
            <Unlink className="h-4 w-4" />
            {t('csvMapperClearAll')}
          </Button>
        ) : null}
      </div>

      {pendingSource ? (
        <Badge variant="outline" className="w-fit border-primary-300 bg-primary-50 text-primary-700">
          <Link2 className="mr-1 h-3 w-3" />
          {t('csvMapperPendingSource', { column: pendingSource })}
        </Badge>
      ) : null}

      <div ref={containerRef} className="relative grid min-h-[16rem] grid-cols-1 gap-6 md:grid-cols-[1fr_auto_1fr]">
        <ConnectorLines links={links} containerRef={containerRef} />

        <div className="relative z-10 flex flex-col gap-2">
          <p className="text-xs font-semibold uppercase tracking-wide text-text-tertiary">
            {t('csvMapperSourceColumns')}
          </p>
          <ul className="flex flex-col gap-2">
            {headers.map((header, headerIndex) => {
              const isPending = pendingSource === header;
              const isUsed = usedHeaders.has(header) && !isPending;
              return (
                <li key={header}>
                  <button
                    type="button"
                    data-source-id={`src-${headerIndex}`}
                    disabled={disabled}
                    onClick={() => handleSourceClick(header)}
                    className={cn(
                      'w-full rounded-lg border px-3 py-2 text-left text-sm transition-colors',
                      isPending && 'border-primary-500 bg-primary-50 text-primary-700',
                      !isPending && isUsed && 'border-border-light bg-background-tertiary text-text-tertiary',
                      !isPending &&
                        !isUsed &&
                        'border-border-light bg-background-card text-text-primary hover:border-primary-300 hover:bg-primary-50/50'
                    )}
                  >
                    {header}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>

        <div className="hidden items-center justify-center md:flex">
          <ArrowRight className="h-5 w-5 text-text-tertiary" aria-hidden />
        </div>

        <div className="relative z-10 flex flex-col gap-2">
          <p className="text-xs font-semibold uppercase tracking-wide text-text-tertiary">
            {t('csvMapperTargetFields')}
          </p>
          <ul className="flex flex-col gap-2">
            {dtoFields.map((field) => {
              const linked = mapping?.[field];
              const label = fieldLabels[field] || field;
              const isRequired = requiredFields?.has?.(field);
              return (
                <li key={field}>
                  <button
                    type="button"
                    data-target-id={`tgt-${field}`}
                    disabled={disabled}
                    onClick={() => handleTargetClick(field)}
                    className={cn(
                      'flex w-full flex-col gap-1 rounded-lg border px-3 py-2 text-left text-sm transition-colors',
                      linked && 'border-success-300 bg-success-50/50',
                      !linked && isRequired && 'border-warning-200',
                      !linked && pendingSource && 'border-dashed border-primary-400 hover:bg-primary-50/40',
                      !linked && !pendingSource && 'border-border-light bg-background-card hover:border-primary-300'
                    )}
                  >
                    <span className="font-medium text-text-primary">
                      {label}
                      {isRequired ? <span className="text-danger-500"> *</span> : null}
                    </span>
                    <span className="text-xs text-text-secondary">
                      {linked || t('csvMapperUnmapped')}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </div>
  );
}

export default CsvColumnConnector;
